import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../hooks/useAuth';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [versionUpdate, setVersionUpdate] = useState(null);
  const { user, token } = useAuth();

  useEffect(() => {
    if (!user || !token) {
      // Disconnect socket if user is not authenticated
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    const initSocket = async () => {
      try {
        const newSocket = io(process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:5001', {
          auth: {
            token: token
          },
          transports: ['websocket', 'polling']
        });

        newSocket.on('connect', () => {
          console.log('Connected to server');
          setConnected(true);
        });

        newSocket.on('disconnect', () => {
          console.log('Disconnected from server');
          setConnected(false);
        });

        newSocket.on('connect_error', (error) => {
          console.error('Connection error:', error);
          setConnected(false);
        });

        // Listen for new notifications
        newSocket.on('new_notification', (notification) => {
          console.log('New notification received:', notification);
          
          // Add to notifications list (with deduplication)
          setNotifications(prev => {
            // Check if notification with same id already exists
            const exists = prev.some(n => n.id === notification.id);
            if (exists) {
              console.warn('Duplicate notification received, ignoring:', notification.id);
              return prev;
            }
            
            const newNotifications = [notification, ...prev];
            
            // Update unread count based on the new notifications array
            const newUnreadCount = newNotifications.filter(n => !n.is_read).length;
            setUnreadCount(newUnreadCount);
            
            return newNotifications;
          });
          
          // Show browser notification if permission granted
          if (Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.message,
              icon: '/logo192.png',
              badge: '/logo192.png',
            });
          }
        });

        // Listen for new messages (conversations — separate from alerts)
        newSocket.on('new_message', (messageEvent) => {
          console.log('New message received:', messageEvent);

          setConversations((prev) => {
            const existingIndex = prev.findIndex(
              (c) => c.id === messageEvent.conversationId
            );
            const existing = existingIndex >= 0 ? prev[existingIndex] : null;
            const wasAlreadyUnread = Boolean(existing?.is_unread);
            const nextStatus = messageEvent.status !== undefined
              ? messageEvent.status
              : existing?.status;
            const shouldMarkUnread = messageEvent.markUnread !== false
              && nextStatus !== 'closed';

            if (shouldMarkUnread && !wasAlreadyUnread) {
              setMessageUnreadCount((count) => count + 1);
            } else if (!shouldMarkUnread && wasAlreadyUnread) {
              setMessageUnreadCount((count) => Math.max(0, count - 1));
            }

            const kind = messageEvent.kind
              || existing?.kind
              || (['patient', 'admission', 'bill'].includes(messageEvent.objectType)
                ? 'task'
                : 'conversation');

            const updatedConversation = {
              ...(existing || {}),
              id: messageEvent.conversationId,
              object_type: messageEvent.objectType || existing?.object_type,
              object_label: messageEvent.objectLabel || existing?.object_label,
              object_link: messageEvent.objectLink || existing?.object_link,
              kind,
              status: nextStatus,
              created_by_user_id: messageEvent.createdByUserId !== undefined
                ? messageEvent.createdByUserId
                : existing?.created_by_user_id,
              is_creator: existing?.is_creator,
              last_message_preview: messageEvent.preview,
              last_message_at: messageEvent.createdAt || new Date().toISOString(),
              is_unread: shouldMarkUnread,
              other_participant: existing?.other_participant
                || (messageEvent.senderName
                  ? { name: messageEvent.senderName }
                  : null),
            };

            if (existingIndex >= 0) {
              const next = [...prev];
              next.splice(existingIndex, 1);
              return [updatedConversation, ...next];
            }
            return [updatedConversation, ...prev];
          });
        });

        // Listen for app version updates
        newSocket.on('app_version_update', (data) => {
          console.log('App version update received:', data);
          setVersionUpdate(data);
        });

        setSocket(newSocket);

        return () => {
          newSocket.disconnect();
        };
      } catch (error) {
        console.error('Error initializing socket:', error);
      }
    };

    initSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [user, token]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        console.log('Notification permission:', permission);
      });
    }
  }, []);

  const markNotificationAsRead = (notificationId) => {
    setNotifications(prev => {
      const updated = prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, is_read: true }
          : notification
      );
      
      // Update unread count based on the updated notifications
      const newUnreadCount = updated.filter(n => !n.is_read).length;
      setUnreadCount(newUnreadCount);
      
      return updated;
    });
  };

  const markAllAsRead = () => {
    setNotifications(prev => {
      const updated = prev.map(notification => ({ ...notification, is_read: true }));
      
      // All notifications are now read, so unread count is 0
      setUnreadCount(0);
      
      return updated;
    });
  };

  const removeNotification = (notificationId) => {
    setNotifications(prev => {
      const updated = prev.filter(notification => notification.id !== notificationId);
      const newUnreadCount = updated.filter(n => !n.is_read).length;
      setUnreadCount(newUnreadCount);
      return updated;
    });
  };

  const markConversationRead = (conversationId) => {
    setConversations((prev) => {
      const wasUnread = prev.find((c) => c.id === conversationId)?.is_unread;
      if (wasUnread) {
        setMessageUnreadCount((count) => Math.max(0, count - 1));
      }
      return prev.map((c) =>
        c.id === conversationId ? { ...c, is_unread: false } : c
      );
    });
  };

  const upsertConversation = (conversation) => {
    setConversations((prev) => {
      const filtered = prev.filter((c) => c.id !== conversation.id);
      return [conversation, ...filtered];
    });
  };

  const value = {
    socket,
    connected,
    notifications,
    unreadCount,
    setNotifications,
    setUnreadCount,
    markNotificationAsRead,
    markAllAsRead,
    removeNotification,
    conversations,
    messageUnreadCount,
    setConversations,
    setMessageUnreadCount,
    markConversationRead,
    upsertConversation,
    versionUpdate,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
